import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FooterContent } from './FooterContent';
import DottedBackground from './DottedBackground';
import { Outlet, useLocation } from 'react-router-dom';
import { ReactComponent as SpygamesLogo } from 'src/shared/components/icons/spygames-logo.svg';
import { Progress } from '../shared/components/progress';
import { PATHS } from '../shared/defines';
import { useScreen } from 'usehooks-ts';
import { useEffect, useMemo, useRef } from 'react';

const routes = [PATHS.CHILD_DETAILS, PATHS.WAIVER_SIGNATURE, PATHS.CHILD_HEIGHT, PATHS.CONFIRM_PERMISSION];
const PROGRESS_MAX = routes.length;

const getProgressStepIndex = (pathname: string) => {
  const path = pathname.split('/')[1];
  return routes.indexOf(path);
};

function Layout() {
  const { pathname } = useLocation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const progressStepIndex = getProgressStepIndex(pathname);

  const { width } = useScreen();

  const dotsContainerWidth = useMemo(() => {
    const n = Math.floor((width - 20) / 26);
    return `${n * 26 + 6}px`;
  }, [width]);

  useEffect(() => {
    scrollContainerRef.current?.scrollTo({
      top: 0,
      left: 0,
      behavior: 'auto',
    });
  }, [pathname]);

  return (
    <>
      <ToastContainer
        className={'bottom-10 absolute z-50 bg-transparent'}
        position="bottom-center"
        autoClose={1000}
        hideProgressBar
        closeButton={false}
        newestOnTop={false}
        pauseOnFocusLoss={false}
      />
      <div
        className="w-full h-full bg-green-dark text-white flex justify-center overflow-y-scroll relative font-aeonik"
        ref={scrollContainerRef}
      >
        <div className="flex-1 hidden sm:flex flex-col overflow-hidden max-h-full h-fit sticky top-0 pl-5 py-3.5 sm:py-5">
          <div className="w-full flex-1 overflow-hidden">
            <DottedBackground
              dotClassName="bg-green-laser"
              columnContainerClassName="flex-row-reverse"
              xCount={40}
              yCount={60}
              className="overflow-hidden hidden sm:inline z-40"
            />
          </div>
        </div>
        <div className="min-h-full h-fit flex flex-col justify-center sm:w-170 w-full sm:px-20 pt-5 px-3.5 overflow-x-hidden">
          <div className="flex w-full sm:mt-5.5 mt-0 flex-col sm:flex-row gap-7.5">
            <div className="flex-1 justify-start">
              <div className="sm:relative absolute flex sm:ml-0 ml-5">
                <SpygamesLogo className="sm:w-52 sm:h-7.5 w-29 h-4.5 text-green-laser " />
              </div>
              <div className="flex w-full justify-between relative sm:hidden">
                <div className="flex flex-col gap-4.5">
                  <div className="w-1.5 h-1.5 bg-green-laser rounded-2.5" />
                  <div className="w-1.5 h-1.5 bg-green-laser rounded-2.5" />
                </div>
                <div className="flex flex-col gap-4.5">
                  <div className="w-1.5 h-1.5 bg-green-laser rounded-2.5" />
                  <div className="w-1.5 h-1.5 bg-green-laser rounded-2.5" />
                </div>
              </div>
            </div>
            {progressStepIndex >= 0 && <Progress value={progressStepIndex} max={PROGRESS_MAX} />}
          </div>

          <div className="sm:w-130 w-full flex-1 flex flex-col gap-10 mt-8">
            <Outlet />
          </div>

          <div className="w-full mt-7.5 pb-5 flex flex-col ">
            <div className="w-full flex justify-center">
              <DottedBackground
                xCount={27}
                yCount={4}
                dotClassName="bg-green-laser w-1.5 h-1.5 rounded-all"
                className="mb-7.5 sm:hidden block"
                style={{ width: dotsContainerWidth }}
              />
            </div>

            <FooterContent />
          </div>
        </div>

        <div className="flex-1 hidden sm:flex flex-col overflow-hidden max-h-full h-fit sticky top-0 pr-5 py-3.5 sm:py-5">
          <div className="w-full flex-1 overflow-hidden">
            <DottedBackground
              xCount={40}
              yCount={60}
              dotClassName="bg-green-laser"
              className="overflow-hidden hidden sm:inline z-40"
            />
          </div>
        </div>
      </div>
    </>
  );
}

export default Layout;
